import { useState } from "react";
import { useAppDispatch } from "../../framework/customStore";
import { useTranslation } from "react-i18next";
import { publishPlannedWorkShifts } from "../../services/plannedWorkShiftService";
import {
    PlannedWorkShiftDto,
    PlannedWorkShiftsPublishDto,
    PlannedWorkShiftState
} from "../../models/plannedWorkShift/plannedWorkShift";
import { IApiError } from "../../services/baseService";
import {
    showErrorMessage,
    showSuccessMessage,
} from "../../models/store/storeActions";
import { reloadPlannedWorkShifts } from "../../store/workShiftPlanningSlice";
import { WorkShiftPlanningTab } from "../../components/workShiftPlanning/WorkShiftPlanningRouter";

export const usePublishShifts = () => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const [saving, setSaving] = useState(false);

    const createPublishDto = (shifts: PlannedWorkShiftDto[]): PlannedWorkShiftsPublishDto => {
        return {
            shifts: shifts
                .filter((s) => !s.isTempInstance)
                .map((s) => ({
                    id: s.id,
                    rowId: s.rowId,
                })),
            recurringShifts: shifts
                .filter((s) => s.isTempInstance)
                .map((s) => ({
                    recurringShiftId: s.recurrence?.recurringShiftId,
                    recurringShiftRowId: s.recurrence?.rowId,
                    employeeId: s.employeeId,
                    vehicleId: s.vehicleId,
                    routeId: s.routeId,
                    startTime: s.startTime,
                    endTime: s.endTime,
                    label: s.label,
                    comment: s.comment,
                })),
        };
    };

    const publishShifts = async(shifts: PlannedWorkShiftDto[]) => {
        // Filter out shifts with null employeeId before publishing
        const validShifts = shifts.filter(s => s.employeeId !== null);

        if (!saving && validShifts.length > 0) {
            setSaving(true);

            try {
                const dto = createPublishDto(validShifts);
                await publishPlannedWorkShifts(dto);
                dispatch(showSuccessMessage(t("common.saveSuccess")));
                dispatch(reloadPlannedWorkShifts());
                return true;
            } catch (error: any) {
                dispatch(
                    showErrorMessage(
                        (error as IApiError)?.status === 409
                            ? t("common.saveConflict")
                            : t("common.saveFailed")
                    )
                );
                return false;
            } finally {
                setSaving(false);
            }
        }
        return false;
    };

    // Publish all shifts in planning state
    const publishAllShifts = (shifts: PlannedWorkShiftDto[]) => {
        const planningShifts = shifts.filter(
            s => s.state === PlannedWorkShiftState.InPlanning
        );
        return publishShifts(planningShifts);
    };

    // Publish a single shift
    const publishSingleShift = (shift: PlannedWorkShiftDto) => {
        return publishShifts([shift]);
    };

    // Publish all shifts for a specific entity
    const publishEntityShifts = (
        shifts: PlannedWorkShiftDto[],
        targetId: string,
        targetType: WorkShiftPlanningTab
    ) => {
        const planningShifts = shifts.filter(
            (s) =>
                s.state === PlannedWorkShiftState.InPlanning &&
            ((targetType === WorkShiftPlanningTab.Employee &&
                s.employeeId === targetId) ||
                (targetType === WorkShiftPlanningTab.Vehicle &&
                    s.vehicleId === targetId) ||
                (targetType === WorkShiftPlanningTab.Route &&
                    s.routeId === targetId))
        );
        return publishShifts(planningShifts);
    };

    return {
        publishAllShifts,
        publishSingleShift,
        publishEntityShifts,
        saving
    };
};